import { QUERY_KEYS } from '@avanta/shared/constants/common';
import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useServerPaginatedTable } from '@avanta/shared/hooks/useServerPaginatedTable';
import { type TableDateRange } from '@avanta/shared/types/table';
import { isLoadingNewData } from '@avanta/shared/utils/table';
import { useQuery } from '@tanstack/react-query';
import { type ColumnDef, type Table, type Updater } from '@tanstack/react-table';
import { format, startOfYear } from 'date-fns';
import { useState } from 'react';
import { EnterprisesService } from 'services/enterprises';
import { useEnterpriseId } from 'stores/user';
import { type EnterpriseInvoice, type GetEnterpriseInvoicesParams } from 'types/enterprises';

const DEFAULT_PAGE_SIZE = 8;

const getDefaultDateRange = (): TableDateRange => ({
  from: startOfYear(new Date()),
  to: new Date(),
});

const toIsoDate = (date: Date): string => format(date, 'yyyy-MM-dd');

export const useFetchInvoices = (
  columns: ColumnDef<EnterpriseInvoice>[]
): {
  table: Table<EnterpriseInvoice>;
  selectedDateRange: TableDateRange | undefined;
  setDateRange: (value: TableDateRange | undefined) => void;
} => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState<TableDateRange | undefined>(
    getDefaultDateRange
  );
  const enterpriseId = useEnterpriseId();
  const debouncedSearch = useDebounce(globalFilter, 500);
  const dateFrom = selectedDateRange?.from ? toIsoDate(selectedDateRange.from) : undefined;
  const dateTo = selectedDateRange?.to ? toIsoDate(selectedDateRange.to) : undefined;

  const params: GetEnterpriseInvoicesParams = {
    offset: pagination.pageIndex,
    limit: pagination.pageSize,
    dateFrom,
    dateTo,
    search: debouncedSearch || undefined,
  };

  const resetPagination = (): void => {
    setPagination(previousPagination =>
      previousPagination.pageIndex === 0
        ? previousPagination
        : { ...previousPagination, pageIndex: 0 }
    );
  };

  const handleGlobalFilterChange = (updater: Updater<string>): void => {
    setGlobalFilter(updater);
    resetPagination();
  };

  const setDateRange = (value: TableDateRange | undefined): void => {
    setSelectedDateRange(value);
    resetPagination();
  };

  const query = useQuery({
    queryKey: [QUERY_KEYS.FETCH_ENTERPRISE_INVOICES, enterpriseId, params],
    queryFn: () => EnterprisesService.getInvoices(enterpriseId, params),
    select: response => response.data,
    placeholderData: previousData => previousData,
    enabled: !!enterpriseId,
  });

  const table = useServerPaginatedTable<EnterpriseInvoice>({
    columns,
    data: query.data?.invoices ?? [],
    totalCount: query.data?.total ?? 0,
    pagination,
    onPaginationChange: setPagination,
    globalFilter,
    onGlobalFilterChange: handleGlobalFilterChange,
    isLoadingData: isLoadingNewData(query),
    pageCountKey: { enterpriseId, search: debouncedSearch, dateFrom, dateTo },
  });

  return {
    table,
    selectedDateRange,
    setDateRange,
  };
};
